import { useDepartmentSearch } from "@/services/commonService";
import { DepartmentRoleType, DepartmentTreeNode } from "@/types/department";
import { Close as CloseIcon, Inbox as InboxIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import Draggable from "react-draggable";

const DraggablePaper = React.forwardRef<HTMLDivElement, any>((props, ref) => {
  const nodeRef = React.useRef<HTMLDivElement>(null);
  React.useImperativeHandle(ref, () => nodeRef.current as HTMLDivElement);

  return (
    <Draggable handle='#draggable-dialog-title' nodeRef={nodeRef}>
      <Paper {...props} ref={nodeRef} />
    </Draggable>
  );
});

interface DepartmentDialogProps {
  open: boolean;
  maxSelected: number;
  items: DepartmentTreeNode[];
  roleType: DepartmentRoleType;
  isCompanyWide: boolean;
  onClose: () => void;
  onApply: (departments: DepartmentTreeNode[]) => void;
}

const DepartmentDialog: React.FC<DepartmentDialogProps> = ({
  open,
  maxSelected,
  onClose,
  onApply,
  // items,
  roleType,
  isCompanyWide = false,
}) => {
  // Set<string | number>으로 선택 관리
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [localSearchTerm, setLocalSearchTerm] = useState("");
  const [onlyHeadDept, setOnlyHeadDept] = useState(false);
  const paperRef = useRef<HTMLDivElement>(null);
  const { data: departments, isLoading } = useDepartmentSearch(
    searchTerm,
    isCompanyWide ? DepartmentRoleType.CC : roleType
  );

  useEffect(() => {
    if (!open) {
      setSearchTerm("");
      setLocalSearchTerm("");
      setOnlyHeadDept(false);
      setSelectedIds(new Set());
    }
  }, [open]);

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearchButtonClick();
    }
  };

  const handleSearchButtonClick = () => {
    setSearchTerm(localSearchTerm);
  };

  const rows = useMemo(
    () =>
      (departments ?? []).map((dept) => ({
        ...dept,
        headCount: dept.id !== 0 ? (dept.headCount ?? 0) : 1, // 전사 선택 가능하도록 headCount 1로 설정
      })),
    [departments]
  );

  const filteredRows = useMemo(
    () => rows.filter((row) => !onlyHeadDept || (row.headCount ?? 0) > 0),
    [rows, onlyHeadDept]
  );

  // 체크박스 비활성화 로직
  const isRowSelectable = (row: DepartmentTreeNode) => {
    const alreadySelected = selectedIds.has(row.id);
    return (row.headCount ?? 0) > 0 && (selectedIds.size < maxSelected || alreadySelected);
  };

  // 전체 선택 가능 여부 확인
  const isAllSelectable = useMemo(() => {
    const selectableRows = filteredRows.filter((row) => isRowSelectable(row));
    return selectableRows.length > 0;
  }, [filteredRows, selectedIds, maxSelected]);

  // 전체 선택 상태 확인
  const isAllSelected = selectedIds.size === maxSelected;

  // 선택 변경 핸들러
  const handleRowSelectionChange = (row: DepartmentTreeNode) => {
    if (!isRowSelectable(row)) return;
    const newSet = new Set(selectedIds);
    if (selectedIds.has(row.id)) {
      newSet.delete(row.id);
    } else {
      if (selectedIds.size >= maxSelected) return;
      newSet.add(row.id);
    }
    setSelectedIds(newSet);
  };

  // 전체 선택 핸들러
  const handleSelectAll = (checked: boolean) => {
    if (!isAllSelectable) return;
    if (checked) {
      // 선택 가능한 행을 순서대로 선택 (이미 선택된 항목은 중복 추가하지 않음)
      const selectableRows = filteredRows
        .filter((row) => isRowSelectable(row) && !selectedIds.has(row.id))
        .slice(0, maxSelected - selectedIds.size);
      const newSet = new Set(selectedIds);
      selectableRows.forEach((row) => newSet.add(row.id));
      setSelectedIds(newSet);
    } else {
      // filteredRows에 있는 모든 row의 id를 selectedIds에서 제거
      const filteredIds = filteredRows.map((row) => row.id);
      const newSet = new Set(selectedIds);
      filteredIds.forEach((id) => newSet.delete(id));
      setSelectedIds(newSet);
    }
  };

  const handleApply = () => {
    // id로 객체 배열 변환
    const allDepartments = departments ?? [];
    const selectedDepartments = allDepartments.filter((dept) => selectedIds.has(dept.id));
    onApply(selectedDepartments);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='sm'
      fullWidth
      PaperComponent={DraggablePaper}
      slotProps={{
        paper: {
          ref: paperRef,
          sx: {
            position: "relative",
            minHeight: "400px",
            minWidth: "650px",
            display: "flex",
            flexDirection: "column",
          },
        },
      }}
    >
      <Box sx={{ width: "100%", height: "100%" }}>
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid",
            borderColor: "divider",
            cursor: "move",
            "&:hover": {
              bgcolor: "action.hover",
            },
          }}
          id='draggable-dialog-title'
        >
          <Typography variant='h6' component='div'>
            부서 선택
          </Typography>
          <IconButton
            aria-label='close'
            onClick={onClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            flex: 1,
            p: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              p: 2,
              borderBottom: "1px solid",
              borderColor: "divider",
              display: "flex",
              gap: 1,
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                border: "1px solid #ccc",
                borderRadius: 1,
                px: 1,
                py: 0.5,
                bgcolor: "#fff",
                display: "flex",
                alignItems: "center",
                height: 40,
                mr: 1.2,
              }}
            >
              <Checkbox size='small' checked={onlyHeadDept} onChange={(_, checked) => setOnlyHeadDept(checked)} />
              <Typography variant='body2' sx={{ mr: 2 }}>
                조직장 있는 부서만
              </Typography>
            </Box>
            <TextField
              size='small'
              sx={{ flex: 1 }}
              placeholder='부서 검색'
              inputProps={{ maxLength: 20 }}
              value={localSearchTerm}
              onChange={(e) => setLocalSearchTerm(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              slotProps={{
                input: {
                  endAdornment: (
                    <>
                      {localSearchTerm && (
                        <InputAdornment position='end'>
                          <IconButton size='small' onClick={() => setLocalSearchTerm("")} edge='end' tabIndex={-1}>
                            <CloseIcon fontSize='small' />
                          </IconButton>
                        </InputAdornment>
                      )}
                      <InputAdornment position='end'>
                        <span style={{ whiteSpace: "nowrap", fontSize: "0.8rem" }}>
                          {`${localSearchTerm?.length ?? 0}/20 자`}
                        </span>
                      </InputAdornment>
                    </>
                  ),
                },
              }}
            />
            <Button variant='contained' onClick={handleSearchButtonClick} sx={{ minWidth: "80px" }}>
              검색
            </Button>
          </Box>
          <Box
            sx={{
              height: 300,
              p: 2,
              pb: 2,
              overflow: "hidden",
            }}
          >
            {isLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                <CircularProgress size={24} />
              </Box>
            ) : filteredRows.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  fontSize: "16px",
                  color: "#888",
                  gap: "8px",
                }}
              >
                <InboxIcon sx={{ fontSize: 30, color: "#888" }} />
                표시할 데이터가 없습니다.
              </Box>
            ) : (
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    px: 2,
                    py: 1,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.default",
                  }}
                >
                  <Checkbox
                    size='small'
                    edge='start'
                    checked={isAllSelected}
                    disabled={!isAllSelectable}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSelectAll(e.target.checked)}
                  />
                  <Typography
                    variant='subtitle2'
                    sx={{
                      fontWeight: 600,
                      flex: 1,
                      textAlign: "left",
                      ml: 1,
                    }}
                  >
                    부서명
                  </Typography>
                  <Typography
                    variant='subtitle2'
                    sx={{
                      fontWeight: 600,
                      width: 120,
                      textAlign: "left",
                    }}
                  >
                    조직장
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, overflow: "auto" }}>
                  <List sx={{ p: 0 }}>
                    {filteredRows.map((row) => (
                      <ListItem
                        key={row.id}
                        disablePadding
                        sx={{
                          borderBottom: "1px solid",
                          borderColor: "divider",
                          "&:last-child": {
                            borderBottom: "none",
                          },
                        }}
                      >
                        <ListItemButton
                          onClick={() => handleRowSelectionChange(row)}
                          disabled={!isRowSelectable(row)}
                          selected={selectedIds.has(row.id)}
                          sx={{
                            height: 48,
                            "&.Mui-selected": {
                              bgcolor: "action.selected",
                            },
                            "&.Mui-disabled": {
                              opacity: 0.5,
                            },
                          }}
                        >
                          <Checkbox
                            size='small'
                            edge='start'
                            checked={selectedIds.has(row.id)}
                            disabled={!isRowSelectable(row)}
                          />
                          <ListItemText
                            primary={row.fullLabel}
                            primaryTypographyProps={{
                              variant: "body2",
                            }}
                          />
                          <Tooltip title={row.headNames ?? "조직장 없음"} placement='top'>
                            <Typography
                              variant='body2'
                              sx={{
                                width: 120,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {row.headNames ?? "-"}
                            </Typography>
                          </Tooltip>
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>

        <Divider />

        <DialogActions
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <Button variant='outlined' onClick={onClose} sx={{ minWidth: "80px" }}>
            취소
          </Button>
          <Button variant='contained' onClick={handleApply} sx={{ minWidth: "80px" }}>
            적용
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default DepartmentDialog;
